import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "wage-ledger-convert-dialog" }
const _hoisted_2 = { class: "wage-ledger-convert-dialog__header" }
const _hoisted_3 = { class: "header-icon" }
const _hoisted_4 = { class: "header-badge" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "wage-ledger-convert-dialog__body" }
const _hoisted_7 = { class: "convert-steps" }
const _hoisted_8 = { class: "convert-actions" }
const _hoisted_9 = { class: "convert-upload-label" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "convert-file-name"
}
const _hoisted_12 = {
  key: 0,
  class: "convert-meta-row"
}
const _hoisted_13 = { class: "meta-item" }
const _hoisted_14 = ["value"]
const _hoisted_15 = { class: "meta-item" }
const _hoisted_16 = ["value"]
const _hoisted_17 = {
  key: 1,
  class: "convert-notes"
}
const _hoisted_18 = {
  key: 2,
  class: "convert-summary"
}
const _hoisted_19 = {
  key: 0,
  class: "ok"
}
const _hoisted_20 = {
  key: 1,
  class: "err"
}
const _hoisted_21 = {
  key: 3,
  class: "convert-map-wrap"
}
const _hoisted_22 = { class: "convert-map-table" }
const _hoisted_23 = {
  key: 0,
  class: "req-mark"
}
const _hoisted_24 = ["value", "onChange"]
const _hoisted_25 = ["value"]
const _hoisted_26 = ["title"]
const _hoisted_27 = {
  key: 4,
  class: "convert-preview-wrap"
}
const _hoisted_28 = { class: "convert-preview-table" }
const _hoisted_29 = { class: "num" }
const _hoisted_30 = { class: "num" }
const _hoisted_31 = {
  key: 5,
  class: "convert-empty"
}
const _hoisted_32 = { class: "wage-ledger-convert-dialog__footer" }

import { computed, reactive, ref, watch } from 'vue';
import * as XLSX from 'xlsx';
import {
  type ColumnMapping,
  type ConvertedRow,
  type WageConvertType,
  analyzeHeaders,
  applyWageColumnDictionary,
  buildSourceColumnOptions,
  convertRows,
  expandMergedCells,
  getConvertColumns,
  mappingCoverage,
  pivotVerticalLedger,
  suggestColumnMapping,
  type WageColumnDictEntry,
} from '@/utils/wageLedgerConvert';
import { commonWageApi } from '@/services/api';


export default /*@__PURE__*/_defineComponent({
  __name: 'WageLedgerConvertModal',
  props: {
    modelValue: { type: Boolean },
    wageType: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFileName = ref('');
const busy = ref(false);
const workbookRef = ref<XLSX.WorkBook | null>(null);
const sheetNames = ref<string[]>([]);
const selectedSheet = ref('');
const rawRows = ref<unknown[][]>([]);
const headerRowIndex = ref(0);
const dataStartIndex = ref(1);
const composedHeaders = ref<string[]>([]);
const sheetNotes = ref<string[]>([]);
const analyzeNotes = ref<string[]>([]);
const dictNote = ref('');
const headerPinned = ref(false);
let dictLoading: Promise<void> | null = null;
const mapping = reactive<ColumnMapping>({});
const convertedRows = ref<ConvertedRow[]>([]);

const recognizeNotes = computed(() =>
  [dictNote.value, ...sheetNotes.value, ...analyzeNotes.value].filter(Boolean)
);

const ensureColumnDict = () => {
  if (!dictLoading) {
    dictLoading = commonWageApi
      .getColumnDict()
      .then((response) => {
        const list = response.data?.data as WageColumnDictEntry[] | undefined;
        if (response.data?.success && Array.isArray(list) && list.length) {
          applyWageColumnDictionary(list);
          dictNote.value = `컬럼 사전 ${list.length}개 항목으로 헤더를 맞춥니다.`;
        }
      })
      .catch(() => {
        dictNote.value = '';
        dictLoading = null;
      });
  }
  return dictLoading;
};

const columns = computed(() => getConvertColumns(props.wageType));
const wageTypeLabel = computed(() => (props.wageType === 'home' ? '방문요양' : '요양시설'));

const sourceOptions = computed(() => buildSourceColumnOptions(composedHeaders.value));
const dataRows = computed(() => rawRows.value.slice(dataStartIndex.value));

const coverage = computed(() => {
  if (!rawRows.value.length) return null;
  return mappingCoverage(mapping, columns.value);
});

const errorRowCount = computed(() => convertedRows.value.filter((r) => r.errors.length).length);
const canDownload = computed(() => convertedRows.value.length > 0 && !!coverage.value?.requiredOk);

const previewDisplayRows = computed(() => convertedRows.value.slice(0, 80));

const headerRowOptions = computed(() => {
  const limit = Math.min(rawRows.value.length, 25);
  const opts = [];
  for (let i = 0; i < limit; i++) {
    const cells = (rawRows.value[i] || [])
      .slice(0, 6)
      .map((c) => String(c ?? '').trim())
      .filter(Boolean)
      .join(' / ');
    opts.push({
      value: i,
      label: `${i + 1}행${cells ? ` — ${cells.slice(0, 48)}` : ''}`,
    });
  }
  return opts;
});

const currentStep = computed(() => {
  if (!rawRows.value.length) return 1;
  if (!convertedRows.value.length) return 2;
  return 3;
});

const stepClass = (step: number) => {
  if (currentStep.value === step) return 'step-chip--active';
  if (currentStep.value > step) return 'step-chip--done';
  return '';
};

const nameKey = computed(() => (props.wageType === 'facility' ? 'cwhd_name' : 'name'));
const birthKey = computed(() => (props.wageType === 'facility' ? 'cwhd_birth' : 'birth'));
const jobKey = computed(() => (props.wageType === 'facility' ? 'ce_position' : 'job'));
const basicKey = computed(() => (props.wageType === 'facility' ? 'cwhd_basic' : 'basic'));
const wageTotalKey = computed(() =>
  props.wageType === 'facility' ? 'cwhd_wage_total' : 'wageTotal'
);

const previewBirth = (row: ConvertedRow) => String(row.values[birthKey.value] ?? '');
const previewJob = (row: ConvertedRow) => String(row.values[jobKey.value] ?? '');
const previewBasic = (row: ConvertedRow) => Number(row.values[basicKey.value]) || 0;
const previewWageTotal = (row: ConvertedRow) => Number(row.values[wageTotalKey.value]) || 0;

const formatNum = (n: number) => {
  if (!n) return '-';
  return n.toLocaleString();
};

const sampleFor = (key: string): string => {
  const idx = mapping[key];
  if (idx == null) return '';
  const first = dataRows.value.find((r) => {
    const v = r?.[idx as number];
    return v != null && String(v).trim() !== '';
  });
  if (!first) return '';
  return String(first[idx as number] ?? '').slice(0, 40);
};

const excelSerialToDate = (n: number): string => {
  try {
    const d = XLSX.SSF.parse_date_code(n);
    if (!d) return '';
    return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  } catch {
    return '';
  }
};

const clearMapping = () => {
  Object.keys(mapping).forEach((k) => delete mapping[k]);
};

const applyMappingObject = (next: ColumnMapping) => {
  clearMapping();
  for (const [k, v] of Object.entries(next)) {
    mapping[k] = v;
  }
};

const rebuildConverted = () => {
  if (!rawRows.value.length || mapping[nameKey.value] == null) {
    convertedRows.value = [];
    return;
  }
  convertedRows.value = convertRows(
    dataRows.value,
    dataStartIndex.value,
    { ...mapping },
    columns.value,
    { excelSerialToDate }
  );
};

const reSuggestMapping = () => {
  const notes: string[] = [];
  const suggested = suggestColumnMapping(
    composedHeaders.value,
    columns.value,
    dataRows.value,
    notes
  );
  analyzeNotes.value = [...analyzeHeaders(
    rawRows.value,
    headerPinned.value ? headerRowIndex.value : null
  ).notes, ...notes];
  applyMappingObject(suggested);
  rebuildConverted();
};

const runAnalyze = () => {
  const analysis = analyzeHeaders(
    rawRows.value,
    headerPinned.value ? headerRowIndex.value : null
  );
  headerRowIndex.value = analysis.headerRowIndex;
  dataStartIndex.value = analysis.dataStartIndex;
  composedHeaders.value = analysis.labels;
  const mapNotes: string[] = [];
  const suggested = suggestColumnMapping(
    analysis.labels,
    columns.value,
    rawRows.value.slice(analysis.dataStartIndex),
    mapNotes
  );
  analyzeNotes.value = [...analysis.notes, ...mapNotes];
  applyMappingObject(suggested);
  rebuildConverted();
};

const loadSheet = (sheetName: string) => {
  const wb = workbookRef.value;
  if (!wb || !sheetName) return;
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return;
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: true,
  }) as unknown[][];
  const expanded = expandMergedCells(matrix, sheet['!merges'] || []);
  const notes: string[] = [];
  if ((sheet['!merges'] || []).length) notes.push('병합된 칸을 펼쳐 읽었습니다.');
  const pivoted = pivotVerticalLedger(expanded);
  if (pivoted.pivoted) notes.push('세로로 쌓인 항목을 임금대장 행으로 돌렸습니다.');
  sheetNotes.value = notes;
  rawRows.value = pivoted.rows;
  headerPinned.value = false;
  runAnalyze();
};

const onSheetChange = () => {
  loadSheet(selectedSheet.value);
};

const onHeaderRowChange = () => {
  headerPinned.value = true;
  runAnalyze();
};

const onMappingChange = (key: string, value: string) => {
  mapping[key] = value === '' ? null : Number(value);
  rebuildConverted();
};

const resetState = () => {
  selectedFileName.value = '';
  workbookRef.value = null;
  sheetNames.value = [];
  selectedSheet.value = '';
  rawRows.value = [];
  headerRowIndex.value = 0;
  dataStartIndex.value = 1;
  composedHeaders.value = [];
  sheetNotes.value = [];
  analyzeNotes.value = [];
  headerPinned.value = false;
  clearMapping();
  convertedRows.value = [];
  if (fileInputRef.value) fileInputRef.value.value = '';
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) resetState();
    else ensureColumnDict();
  }
);

watch(
  () => props.wageType,
  () => {
    if (rawRows.value.length) reSuggestMapping();
  }
);

const close = () => {
  emit('update:modelValue', false);
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!['xlsx', 'xls'].includes(ext || '')) {
    alert('xlsx 또는 xls 파일만 가능합니다.');
    return;
  }

  try {
    busy.value = true;
    selectedFileName.value = file.name;
    await ensureColumnDict();
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true });
    workbookRef.value = wb;
    sheetNames.value = wb.SheetNames.slice();
    selectedSheet.value = wb.SheetNames[0] || '';
    loadSheet(selectedSheet.value);
  } catch (e: any) {
    console.error(e);
    alert(`엑셀을 읽는 중 오류: ${e?.message || '알 수 없는 오류'}`);
    resetState();
  } finally {
    busy.value = false;
  }
};

const downloadConverted = () => {
  if (!canDownload.value) return;
  const cols = columns.value;
  const headers = cols.map((c) => c.header);
  const aoa: (string | number)[][] = [headers];
  for (const row of convertedRows.value) {
    aoa.push(cols.map((c) => row.values[c.key] ?? (c.type === 'number' ? 0 : '')));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '임금마이그레이션');
  const label = props.wageType === 'home' ? '방문요양' : '요양시설';
  XLSX.writeFile(wb, `카이온_임금대장_치환_${label}.xlsx`);
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": __props.modelValue,
    "max-width": "1080",
    persistent: "",
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (emit('update:modelValue', $event)))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_v_icon, {
              size: "18",
              color: "white"
            }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode("mdi-swap-horizontal", -1)
              ]))]),
              _: 1
            })
          ]),
          _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "header-title" }, "임금대장 치환", -1)),
          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "header-badge header-badge--test" }, "테스트", -1)),
          _createElementVNode("span", _hoisted_4, _toDisplayString(wageTypeLabel.value), 1),
          _createElementVNode("button", {
            type: "button",
            class: "wage-ledger-convert-dialog__close",
            disabled: busy.value,
            "aria-label": "닫기",
            onClick: close
          }, [
            _createVNode(_component_v_icon, { size: "18" }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("mdi-close", -1)
              ]))]),
              _: 1
            })
          ], 8, _hoisted_5)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[16] || (_cache[16] = _createElementVNode("p", { class: "convert-intro" }, [
            _createTextVNode(" 제목 줄, 병합, 두세 줄 헤더, 같은 항목명이 근로자·기관에 두 번 있는 임금대장도 열 이름을 읽어서 "),
            _createElementVNode("strong", null, "카이온 임금대장 양식"),
            _createTextVNode("으로 바꿉니다. DB에는 저장하지 않으며, 변환 결과 엑셀을 내려받을 수 있습니다. ")
          ], -1)),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", {
              class: _normalizeClass(["step-chip", stepClass(1)])
            }, "1. 엑셀 선택", 2),
            _createElementVNode("span", {
              class: _normalizeClass(["step-chip", stepClass(2)])
            }, "2. 컬럼 매핑", 2),
            _createElementVNode("span", {
              class: _normalizeClass(["step-chip", stepClass(3)])
            }, "3. 미리보기·다운로드", 2)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, [
              _createElementVNode("input", {
                ref_key: "fileInputRef",
                ref: fileInputRef,
                type: "file",
                accept: ".xlsx,.xls",
                class: "convert-upload-input",
                disabled: busy.value,
                onChange: onFileSelected
              }, null, 40, _hoisted_10),
              _createVNode(_component_v_btn, {
                variant: "flat",
                class: "btn-search",
                size: "small",
                tag: "span",
                disabled: busy.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { size: "14" }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("mdi-upload", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[8] || (_cache[8] = _createTextVNode(" 엑셀 선택 ", -1))
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            (selectedFileName.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(selectedFileName.value), 1))
              : _createCommentVNode("", true),
            (rawRows.value.length)
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 1,
                  variant: "outlined",
                  size: "small",
                  class: "btn-refresh",
                  disabled: busy.value,
                  onClick: reSuggestMapping
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { size: "14" }, {
                      default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                        _createTextVNode("mdi-auto-fix", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[10] || (_cache[10] = _createTextVNode(" 자동매핑 다시 ", -1))
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          (sheetNames.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  _cache[11] || (_cache[11] = _createElementVNode("span", { class: "meta-label" }, "시트", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedSheet).value = $event)),
                    class: "meta-select",
                    onChange: onSheetChange
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sheetNames.value, (name) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: name,
                        value: name
                      }, _toDisplayString(name), 9, _hoisted_14))
                    }), 128))
                  ], 544), [
                    [_vModelSelect, selectedSheet.value]
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _cache[12] || (_cache[12] = _createElementVNode("span", { class: "meta-label" }, "헤더 행", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((headerRowIndex).value = $event)),
                    class: "meta-select",
                    onChange: onHeaderRowChange
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerRowOptions.value, (opt) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: opt.value,
                        value: opt.value
                      }, _toDisplayString(opt.label), 9, _hoisted_16))
                    }), 128))
                  ], 544), [
                    [
                      _vModelSelect,
                      headerRowIndex.value,
                      void 0,
                      { number: true }
                    ]
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (recognizeNotes.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(recognizeNotes.value.join(' ')), 1))
            : _createCommentVNode("", true),
          (coverage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _createElementVNode("span", null, "매핑 " + _toDisplayString(coverage.value.mapped) + "/" + _toDisplayString(coverage.value.total), 1),
                _createElementVNode("span", {
                  class: _normalizeClass(coverage.value.requiredOk ? 'ok' : 'err')
                }, " 필수(성명) " + _toDisplayString(coverage.value.requiredOk ? 'OK' : '미매핑'), 3),
                (convertedRows.value.length)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_19, "변환 " + _toDisplayString(convertedRows.value.length) + "명", 1))
                  : _createCommentVNode("", true),
                (errorRowCount.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_20, "오류 " + _toDisplayString(errorRowCount.value) + "건", 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (sourceOptions.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createElementVNode("table", _hoisted_22, [
                  _cache[14] || (_cache[14] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "카이온 항목"),
                      _createElementVNode("th", null, "원본 열"),
                      _createElementVNode("th", null, "샘플")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (col) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: col.key,
                        class: _normalizeClass({
                  'row-mapped': mapping[col.key] != null,
                  'row-required-miss': col.required && mapping[col.key] == null,
                })
                      }, [
                        _createElementVNode("td", null, [
                          _createTextVNode(_toDisplayString(col.header) + " ", 1),
                          (col.required)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_23, "*"))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", null, [
                          _createElementVNode("select", {
                            class: "map-select",
                            value: mapping[col.key] == null ? '' : String(mapping[col.key]),
                            onChange: ($event: any) => (onMappingChange(col.key, ($event.target as HTMLSelectElement).value))
                          }, [
                            _cache[13] || (_cache[13] = _createElementVNode("option", { value: "" }, "— 매핑 안 함 —", -1)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sourceOptions.value, (opt) => {
                              return (_openBlock(), _createElementBlock("option", {
                                key: `${col.key}-${opt.index}`,
                                value: String(opt.index)
                              }, _toDisplayString(opt.label), 9, _hoisted_25))
                            }), 128))
                          ], 40, _hoisted_24)
                        ]),
                        _createElementVNode("td", {
                          class: "sample-cell",
                          title: sampleFor(col.key)
                        }, _toDisplayString(sampleFor(col.key)), 9, _hoisted_26)
                      ], 2))
                    }), 128))
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (convertedRows.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                _createElementVNode("table", _hoisted_28, [
                  _cache[15] || (_cache[15] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "행"),
                      _createElementVNode("th", null, "성명"),
                      _createElementVNode("th", null, "생년월일"),
                      _createElementVNode("th", null, "직종"),
                      _createElementVNode("th", null, "기본급"),
                      _createElementVNode("th", null, "과세합계"),
                      _createElementVNode("th", null, "상태")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewDisplayRows.value, (row) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: row.rowNum,
                        class: _normalizeClass({ 'row-error': row.errors.length })
                      }, [
                        _createElementVNode("td", null, _toDisplayString(row.rowNum), 1),
                        _createElementVNode("td", null, _toDisplayString(row.name), 1),
                        _createElementVNode("td", null, _toDisplayString(previewBirth(row) || '-'), 1),
                        _createElementVNode("td", null, _toDisplayString(previewJob(row) || '-'), 1),
                        _createElementVNode("td", _hoisted_29, _toDisplayString(formatNum(previewBasic(row))), 1),
                        _createElementVNode("td", _hoisted_30, _toDisplayString(formatNum(previewWageTotal(row))), 1),
                        _createElementVNode("td", null, _toDisplayString(row.errors.length ? row.errors.join(', ') : 'OK'), 1)
                      ], 2))
                    }), 128))
                  ])
                ])
              ]))
            : (selectedFileName.value && !busy.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_31, " 성명 열이 매핑되면 미리보기가 표시됩니다. "))
              : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_32, [
          _createVNode(_component_v_btn, {
            variant: "text",
            class: "btn-close",
            disabled: busy.value,
            onClick: close
          }, {
            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
              _createTextVNode("닫기", -1)
            ]))]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_v_spacer),
          _createVNode(_component_v_btn, {
            variant: "flat",
            class: "btn-save",
            disabled: busy.value || !canDownload.value,
            onClick: downloadConverted
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, { size: "14" }, {
                default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                  _createTextVNode("mdi-download", -1)
                ]))]),
                _: 1
              }),
              _cache[19] || (_cache[19] = _createTextVNode(" 카이온 양식으로 다운로드 ", -1))
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})